import { Injectable } from '@angular/core';

import { SpinnerComponent } from '../spinner/spinner.component';
import { PopoverService } from '../popover/popover.service';
import { ToastFacade } from '../toast/toast.facade';

type UIPromiseOptions = {
  success?: {
    title: string;
    message: string;
  };
  error?: {
    title: string;
    message: string;
  };
  loading?: boolean;
  loadingMessage?: string;
  errorHandler?: (error: Error) => Promise<void> | void;
  timeout?: number;
};

@Injectable({
  providedIn: 'root',
})
export class UiPromiseService {

  private popovers: HTMLIonPopoverElement[] = [];

  constructor(
    private readonly popoverService: PopoverService,
    private readonly toastFacade: ToastFacade,
  ) {
    this.hide = this.hide.bind(this);
  }

  async spinner(message?: string): Promise<{ hide: () => Promise<void> }> {
    const popoverRef = await this.popoverService.simple({
      component: SpinnerComponent,
      translucent: true,
      componentProps: { message },
      cssClass: ['popover-spinner'],
    });

    this.popovers.push(popoverRef);

    return {
      hide: () => this.hide(popoverRef),
    };
  }

  async hide(popoverRef: HTMLIonPopoverElement): Promise<void> {
    await popoverRef.dismiss();
    const index = this.popovers.indexOf(popoverRef);
    if (index > -1) {
      this.popovers.splice(index, 1);
    }
  }

  async hideAll(): Promise<void> {
    await Promise.all(this.popovers.map(popover => popover.dismiss()));
    this.popovers = [];
  }

  async run<T>(func: () => Promise<T>, opt: UIPromiseOptions = {}): Promise<{ error?: any; success?: T }> {
    if (opt.loading) {
      await this.spinner(opt.loadingMessage);
    }
    try {
      const timeout = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error('Timeout'));
        }, opt.timeout || 30000);
      });

      const success = await Promise.race([func(), timeout]);

      if (opt.success) {
        const { title, message } = opt.success;
        await this.toastFacade.present(message, title, { severity: 'success', mobileMode: 'single', browserMode: 'simple' });
      }
      return { success };
    } catch (error) {
      if (opt.errorHandler) {
        await opt.errorHandler(error as Error);
      }
      if (opt.error) {
        const { title: header, message } = opt.error;
        await this.toastFacade.present(message, header, { severity: 'error', mobileMode: 'single', browserMode: 'simple' });
      }
      return { error };
    } finally {
      await this.hideAll();
    }
  }
}
